import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AppContext } from './contexts/AppContext';

// Helper: Toast Notification Component
const Toast = ({ message }) => {
    if (!message) return null;
    return <div className="toast-notification">{message}</div>;
};

// Helper: Fetches with retry and timeout logic
async function fetchWithRetry(url, options, retries = 2, timeout = 30000) {
    for (let i = 0; i <= retries; i++) {
        try {
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), timeout);
            const response = await fetch(url, { ...options, signal: controller.signal });
            clearTimeout(id);
            if (!response.ok) throw new Error(`Server error: ${response.status}`);
            return await response.json();
        } catch (error) {
            if (i === retries) {
                console.error(`Fetch failed after ${retries} retries:`, error);
                throw error;
            }
            console.log(`Attempt ${i + 1} failed, retrying...`);
        }
    }
}

const CodeBlock = React.memo(({ className, children }) => {
    const [isCopied, setIsCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(String(children));
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    }, [children]);

    return (
        <div className="code-block-wrapper">
            <div className="code-block-header">
                <span>{language}</span>
                <button onClick={handleCopy}>{isCopied ? '✓ 복사됨' : '복사'}</button>
            </div>
            <SyntaxHighlighter children={String(children).replace(/\n$/, '')} style={vscDarkPlus} language={language} PreTag="div" />
        </div>
    );
});

const MessageContent = React.memo(({ content, searchQuery }) => {
    const highlightText = (text) => {
        if (!searchQuery || !text) return text;
        try {
            const regex = new RegExp(`(${searchQuery})`, 'gi');
            return text.split(regex).map((part, i) =>
                regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
            );
        } catch (error) { return text; }
    };

    return (
        <ReactMarkdown
            children={content}
            remarkPlugins={[remarkGfm]}
            components={{
                p: ({ node, ...props }) => {
                    const textContent = node.children.map(child => child.value || '').join('');
                    return <p>{highlightText(textContent)}</p>;
                },
                code: ({ node, inline, className, children, ...props }) =>
                    !inline ? <CodeBlock className={className}>{children}</CodeBlock> : <code className={className} {...props}>{children}</code>,
            }}
        />
    );
});

const MessageActions = ({ text }) => {
    const [isCopied, setIsCopied] = useState(false);
    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    }, [text]);

    return (
        <div className="message-actions">
            <button onClick={handleCopy} className={`action-icon-button ${isCopied ? 'copied' : ''}`} title="복사">
                {isCopied ? '✓' : '📋'}
            </button>
        </div>
    );
};


function ChatWindow({ userId, userData, initialMessages, onLogout, onUserDataUpdate }) {
    const [messages, setMessages] = useState(initialMessages || []);
    const [inputValue, setInputValue] = useState('');
    const [isAnswering, setIsAnswering] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [lastUserMessageId, setLastUserMessageId] = useState(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const chatWindowRef = useRef(null);
    const textareaRef = useRef(null);
    const lastScrollTop = useRef(0);
    const { theme, toggleTheme, fontSize, handleFontSizeChange } = useContext(AppContext);
    const API_URL = process.env.REACT_APP_API_URL || '';

    const isMobile = useMemo(() => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []);

    const scrollToBottom = (behavior = 'auto') => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTo({ top: chatWindowRef.current.scrollHeight, behavior });
        }
    };
    
    useEffect(() => {
        scrollToBottom('auto');
    }, []);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [inputValue]);

    useEffect(() => {
        if (!isAnswering && textareaRef.current) {
            const focusTimeout = isMobile ? 100 : 0;
            setTimeout(() => textareaRef.current.focus(), focusTimeout);
        }
    }, [isAnswering, isMobile]);

    // --- [수정] 스마트 스크롤 로직 (입력창 가림 방지 기준 적용) ---
    useEffect(() => {
        if (!chatWindowRef.current || messages.length === 0) return;

        const lastMessage = messages[messages.length - 1];
        if (!lastMessage || lastMessage.sender !== 'ai' || !lastMessage.id) return;

        const lastAiMessageElement = document.getElementById(lastMessage.id);
        const inputAreaContainer = document.querySelector('.input-area-container');
        if (!lastAiMessageElement || !inputAreaContainer) return;

        const chatRect = chatWindowRef.current.getBoundingClientRect();
        const lastAiMessageRect = lastAiMessageElement.getBoundingClientRect();
        
        // 스크롤이 시작되어야 하는 '기준선'을 입력창의 상단으로 설정
        const scrollTriggerLine = chatRect.bottom - inputAreaContainer.offsetHeight - 20; // 20px 추가 여유

        // AI 메시지의 끝이 기준선보다 아래에 있을 경우에만 스크롤 실행
        if (lastAiMessageRect.bottom > scrollTriggerLine) {
            const userMessageElement = document.getElementById(lastUserMessageId);
            if (userMessageElement) {
                userMessageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                scrollToBottom('smooth');
            }
        }
        
    }, [messages, lastUserMessageId]);


    const sendMessage = async (messageText) => {
        const text = (messageText || '').trim();
        if (!text) return;

        const userMessage = { text, sender: 'user', id: `user-${Date.now()}` };
        setLastUserMessageId(userMessage.id);
        
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsAnswering(true);

        setTimeout(() => scrollToBottom('smooth'), 0);

        try {
            const data = await fetchWithRetry(`${API_URL}/api/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, message: { text, sender: 'user' } })
            });

            if (!data) throw new Error("No data received from server.");
            if (data.updatedUserData) onUserDataUpdate(data.updatedUserData);

            if (data.replies && Array.isArray(data.replies)) {
                const aiMessages = data.replies.map(reply => ({ ...reply, id: `ai-${Date.now()}-${Math.random()}` }));
                setMessages(prev => [...prev, ...aiMessages]);
            } else {
                throw new Error("Invalid response format from server.");
            }
        } catch (error) {
            console.error("Message sending error:", error);
            const errorMessage = { id: `ai-${Date.now()}`, text: '오류가 발생했습니다. 메시지 전송에 실패했습니다.', sender: 'ai' };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsAnswering(false);
        }
    };

    const handleEnterPress = (event) => {
        if (isMobile) {
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
            event.preventDefault();
            sendMessage(inputValue);
        }
    };
    
    const showToast = (message) => {
        setToastMessage(message);
        setTimeout(() => setToastMessage(''), 2500);
    };

    const handleCopyId = () => {
        navigator.clipboard.writeText(userId);
        showToast('ID가 복사되었습니다.');
    };

    const filteredMessages = useMemo(() => {
        if (!searchQuery) return messages;
        return messages.filter(msg => (msg.text || '').toLowerCase().includes(searchQuery.toLowerCase()));
    }, [messages, searchQuery]);

    const handleScroll = () => {
        if (chatWindowRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
            setShowScrollButton(scrollHeight - scrollTop > clientHeight * 1.5);
            if (scrollTop > lastScrollTop.current && scrollTop > 50) {
                setIsHeaderVisible(false);
            } else {
                setIsHeaderVisible(true);
            }
            lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
        }
    };

    return (
        <>
            <Toast message={toastMessage} />
            {isSearchOpen && (
               <div className="search-modal-overlay" onClick={() => setIsSearchOpen(false)}>
                   <div className="search-modal-content" onClick={e => e.stopPropagation()}>
                       <h3>대화 내용 검색</h3>
                       <input
                           type="text" placeholder="검색어를 입력하세요..."
                           value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus
                       />
                   </div>
               </div>
            )}
            {isSettingsOpen && (
                <div className="search-modal-overlay" onClick={() => setIsSettingsOpen(false)}>
                    <div className="search-modal-content" onClick={e => e.stopPropagation()}>
                        <h3>설정</h3>
                        <div className="settings-id-display">
                            <p>회원님의 고유 ID입니다.</p>
                            <div className="id-container">
                                <span>{userId}</span>
                                <button onClick={handleCopyId} className="id-copy-button" title="ID 복사">📋</button>
                            </div>
                        </div>
                        <button className="new-user-button logout-button" onClick={onLogout}>로그아웃</button>
                    </div>
                </div>
            )}

            <div className="chat-window" style={{ fontSize: `${fontSize}px` }}>
                <div className={`chat-header ${isHeaderVisible ? 'visible' : ''}`}>
                    <div className="chat-header-left">
                        <div className="chat-header__title">
                            {userData?.ai_name ? `EGO (${userData.ai_name}) 시스템` : 'EGO Standard 1.0v'}
                        </div>
                    </div>
                    <div className="chat-header-right">
                        <button onClick={() => handleFontSizeChange('decrease')} className="header-button" title="글자 작게">-</button>
                        <button onClick={() => handleFontSizeChange('increase')} className="header-button" title="글자 크게">+</button>
                        <button onClick={() => setIsSearchOpen(true)} className="header-button" title="검색">🔍</button>
                        <button onClick={toggleTheme} className="header-button" title="테마 변경">{theme === 'light' ? '🌙' : '☀️'}</button>
                        <button onClick={() => setIsSettingsOpen(true)} className="header-button" title="설정">⚙️</button>
                    </div>
                </div>

                <div className="messages" ref={chatWindowRef} onScroll={handleScroll}>
                    {filteredMessages.length === 0 && !isAnswering ? (
                         <div className="welcome-screen"><h1>안녕하세요, {userData?.name || '사용자'}님.</h1><p>무엇을 도와드릴까요?</p></div>
                    ) : (
                        filteredMessages.map((message) => (
                            <div id={message.id} key={message.id || `msg-${Math.random()}`} className={`message-container ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}>
                                <div className={`message-bubble ${message.text && message.text.length > 80 ? 'wide' : ''}`}>
                                    <MessageContent content={message.text || ''} searchQuery={searchQuery} />
                                </div>
                                {message.text && <MessageActions text={message.text} />}
                            </div>
                        ))
                    )}
                    {isAnswering && (
                        <div className="message-container ai-message">
                            <div className="message-bubble">
                                <div className="typing-indicator">
                                    딥러닝 분석중<span className="dots"></span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <button className={`scroll-to-bottom ${showScrollButton ? 'visible' : ''}`} onClick={() => scrollToBottom('smooth')} title="맨 아래로">↓</button>

                <div className="input-area-container">
                    <div className="input-area">
                        <textarea
                            ref={textareaRef}
                            placeholder="메시지를 입력하세요..."
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            onKeyDown={handleEnterPress}
                            disabled={isAnswering}
                            rows={1}
                            autoFocus
                        />
                        <button onClick={() => sendMessage(inputValue)} disabled={!inputValue.trim() || isAnswering}>➤</button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChatWindow;
